import { AbortController } from 'node-abort-controller';
import Spinner from '@/icons/spinner';
import NetworkService from '@/services/network.service';
import { AppObject, Property } from '@/types';
import {
  IPropertyResponseItem,
  sentryCaptureException,
  transformObjectToQuery,
  transformProperty,
} from '@/utility';
import useDebounce from '@/utility/search';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { ClickAway } from '../ui/click-away';
import { Input } from '../ui/input';
import { useTranslation } from '@/hooks/use-translation';

const MIN_CHARACTER = 3;

function ListingItem({
  property,
  onClick,
}: {
  property: Property;
  onClick: (item: Property) => void;
}) {
  return (
    <li
      className="w-full  cursor-pointer transform duration-200  px-4 py-2 hover:bg-gray-50"
      key={property.propertyNumber}
      onClick={() => onClick(property)}
    >
      <div>
        <div className="flex items-center justify-between space-x-4">
          <div className="flex flex-row items-center space-x-4 flex-1">
            <div className="text-gray-800 font-bold text-sm flex-1">
              {property.name}
            </div>
            <div className="text-gray-400 text-sm">
              {property.propertyNumber}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

interface AutoCompleteCompareProps {
  onSelectPropertyNumber: (propertyNumber: string) => void;
}

interface IAutocompleteData {
  properties: Property[];
}

const initialState: IAutocompleteData = {
  properties: [],
};

const client = new NetworkService();

interface ICancelToken {
  current: AbortController;
}

const fetchAutocompleteData = async (
  value: string,
  locale: string,
  cancelToken?: ICancelToken
): Promise<IAutocompleteData> => {
  if (cancelToken?.current) {
    cancelToken.current.abort();
  }
  cancelToken.current = client.getCancelToken();
  if (!value || value.trim().length < MIN_CHARACTER) {
    return initialState;
  }

  const query: AppObject = {
    keywords: value,
    status: ['A', 'U'],
    resultsPerPage: '20',
    fields: 'propertyNumber,address.*,permissions.*',
  };

  const response = await client.get<{ listings: IPropertyResponseItem[] }>(
    `/api/repliers/listings/?${transformObjectToQuery(query)}`,
    cancelToken.current.signal
  );

  const properties = response.listings.map((item) =>
    transformProperty(item, locale)
  );

  return {
    properties,
  };
};

export const PropertyAutocomplete: React.FC<AutoCompleteCompareProps> = ({
  onSelectPropertyNumber: onSelectPropertyNumber,
}) => {
  const [show, setShow] = useState(false);
  const [value, setValue] = useState('');
  const { locale } = useTranslation();

  const [data, setData] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const debounceSearch = useDebounce(value);
  const cancelToken = useRef(client.getCancelToken());

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const response = await fetchAutocompleteData(
          debounceSearch,
          locale,
          cancelToken
        );
        setData(response);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error(err);
          sentryCaptureException(err);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [debounceSearch, locale]);

  const toggleDropdown = () => {
    setShow((prev) => !prev);
  };

  const openDropdown = () => {
    if (!show) {
      setShow(true);
    }
  };

  const handleClickAway = () => {
    onBlur();
    if (show) {
      setShow(false);
    }
  };

  const onBlur = () => {
    if (!show) return;
  };

  const handleLinkClick = (property: Property) => {
    onSelectPropertyNumber(property.propertyNumber);
    toggleDropdown();
    setValue('');
  };

  return (
    <ClickAway
      onClickAway={handleClickAway}
      className="relative w-120"
      value={show}
    >
      {/* <div className="mb-1 text-xs text-gray-400">
        <span></span>
      </div> */}

      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClick={openDropdown}
        onBlur={onBlur}
        placeholder="Search property to compare"
        margin={'dense'}
        variant="outlined"
      />

      {Boolean(value) && value.trim().length >= MIN_CHARACTER && (
        <ul
          className={clsx(
            'origin-top-right absolute -left-20 -right-20 md:left-0 md:right-0 mt-2 bg-white shadow-lg border rounded z-50 overflow-auto',
            { hidden: !show },
            { block: show }
          )}
          style={{ maxHeight: '35rem' }}
        >
          {loading ? (
            <li className="w-full transform duration-200 text-gray-400 px-4 py-4 flex items-center justify-center cursor-wait">
              <Spinner className="h-6 w-6 text-blue-500 spin" />
            </li>
          ) : !loading && !data.properties.length ? (
            <li className="w-full cursor-not-allowed transform duration-200 px-4 py-2">
              <p className="text-gray-400 text-center py-4">No data found</p>
            </li>
          ) : (
            <>
              {data.properties.map((property) => (
                <ListingItem
                  key={property.propertyNumber}
                  property={property}
                  onClick={handleLinkClick}
                />
              ))}
            </>
          )}
        </ul>
      )}
    </ClickAway>
  );
};
